import { referenceFieldColors } from '@/theme'
import { ReferenceFields } from 'honeygrid-types'

import type { AggregatedDemographics } from 'honeygrid-types'
import type { FillLayerSpecification, LineLayerSpecification } from 'mapbox-gl'

const MinOpacity = 0.25
const MaxOpacity = 0.7

export const getHoneygridFillPaintConfig = (
	referenceField: string = ReferenceFields.Households,
	aggregatedDemographics?: AggregatedDemographics
): FillLayerSpecification['paint'] => {
	const fieldGetterExpression = [
		'case',
		['!', ['has', referenceField]],
		0, // if the feature does not have the property, return 0
		['==', ['get', referenceField], null],
		0, // if the property's value is null, return 0
		['get', referenceField], // otherwise, return the property's value
	]

	const average = aggregatedDemographics?.averages[referenceField] ?? 0

	// Get color for field - use custom field color logic or fallback to built-in
	const getFieldColor = (field: string) => {
		// For custom fields (starting with ds_), use default color
		if (field.startsWith('ds_')) {
			return (
				referenceFieldColors.default?.main ||
				referenceFieldColors[ReferenceFields.CustomerCount]?.main
			)
		}
		// For built-in fields, use the specific color
		return (
			referenceFieldColors[field as ReferenceFields]?.main ??
			referenceFieldColors[ReferenceFields.CustomerCount]?.main
		)
	}

	return {
		'fill-color': getFieldColor(referenceField),
		'fill-opacity':
			average === 0
				? 0
				: [
						'interpolate',
						['linear'],
						fieldGetterExpression,
						0,
						0,
						average,
						MinOpacity,
						average * 2,
						MaxOpacity,
					],
		'fill-opacity-transition': { duration: 500 },
	}
}

export const getHoneygridLinePaintConfig = (
	referenceField: string = ReferenceFields.Households,
	easySelectionIndex: number
): LineLayerSpecification['paint'] => {
	const fieldIndexGetterExpression = [
		'case',
		['has', `${referenceField}Index`],
		['get', `${referenceField}Index`],
		0,
	]

	return {
		'line-color': [
			'case',
			['<=', fieldIndexGetterExpression, easySelectionIndex],
			referenceFieldColors[ReferenceFields.Households]?.main,
			'#000',
		],
		'line-opacity': ['case', ['<=', fieldIndexGetterExpression, easySelectionIndex], 0.05, 0.05],
	}
}
